import { Archive2 } from "@/components/icons/customIcons.tsx";
import { TeamsIcon } from "@/components/icons/TeamsIcon.tsx";
import { SlackIcon } from "@/components/icons/SlackIcon.tsx";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import { getThreadStatus } from "@/components/threads/util.ts";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import type { GetThreadResp, ThreadChannel } from "@/types/thread.ts";
import { THREAD_STATUS_LABEL } from "@/types/threadCardStatus.ts";
import {
  Bell,
  CalendarX,
  CircleCheck,
  Clock3,
  Hourglass,
  type LucideIcon,
  MessageCircle,
  PauseCircle,
  Zap,
} from "lucide-react";

// ── Generic ThreadBadge ─────────────────────────────────────────────────

type ThreadBadgeProps = {
  icon: LucideIcon | React.FC<{ className?: string }>;
  label: string;
  tooltip: string;
  compact?: boolean;
  className?: string;
  /**
   * Compact-only: render as a bare tinted glyph at the scale of the icon
   * buttons it sits beside, with no chip and no hover fill. This is an
   * indicator, not a control — a background that appears under the cursor
   * would promise a click that does not exist.
   */
  headerScale?: boolean;
};

/**
 * Reusable badge with Tooltip. Defaults to an inline `<icon + label>` render
 * — status reads as colored text next to its icon, consistent across the
 * card, row, and thread detail header.
 */
const ThreadBadge = ({
  icon: Icon,
  label,
  tooltip,
  compact = false,
  className,
  headerScale = false,
}: ThreadBadgeProps) => {
  // A mark on the row, not a control in it: no chip, no target, no hover fill.
  // It sits a step below the 20px action glyphs beside it — at matching size its
  // full colour reads heavier than they do and pulls the eye off them. It keeps
  // that colour while they rest dimmed, since this one is an alert and dimming
  // it to match would be the wrong kind of consistency.
  if (compact && headerScale) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* `px-1.5` matches the padding an icon button holds around its glyph,
              so this contributes the same 6px to the gap on either side of it.
              Without it a bare glyph crowds its neighbour while two padded
              buttons sit twice as far apart at the identical flex gap. */}
          {/* `role="img"` + the tooltip text as the label: the span is not
              focusable, so a Tooltip alone names it for nobody, and below the
              pill's breakpoint this is the only copy of the mark in the DOM. */}
          <span
            className={cn("inline-flex h-8 shrink-0 items-center justify-center px-1.5", className)}
            role="img"
            aria-label={tooltip}
          >
            <Icon className="size-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            // items-baseline + icon self-center: the badge exposes the label
            // text baseline to its parent flex line while keeping the icon
            // visually centered with the label.
            "inline-flex items-baseline gap-1 text-xs font-medium whitespace-nowrap",
            className,
          )}
        >
          <Icon className="size-3 self-center" />
          {!compact && label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

// ── Status config ───────────────────────────────────────────────────────
//
// `className` is the hue the card view and detail header wear — status as
// colored text beside its icon — and it carries the same hue the list row's
// mark does, so one thread does not report two different colors depending on
// which surface you are looking at. The marks live in `compactStatusMark`
// below; change a hue there and change it here too.
//
// Three tiers, and the mark table below draws from the same three: brand for
// the states parked on the user, amber for the one the agent is actively
// working, and one zinc-400 for the finished pair. The glyph says which state
// within a tier it is, so shades a step apart would only suggest a distinction
// that does not exist.

// Labels come from the shared `THREAD_STATUS_LABEL` (single source of truth,
// shared with the thread artifact card); icons + colors stay web-specific.
export const threadStatusConfig = {
  completed: {
    label: THREAD_STATUS_LABEL.completed,
    tooltip: "Task was completed",
    icon: CircleCheck,
    className: "text-muted-foreground",
  },
  needsAttention: {
    label: THREAD_STATUS_LABEL.needsAttention,
    tooltip: "Task requires your input",
    icon: Bell,
    className: "text-primary",
  },
  inProgress: {
    label: THREAD_STATUS_LABEL.inProgress,
    tooltip: "Task is in progress",
    icon: Clock3,
    className: "text-amber-600",
  },
  waiting: {
    label: THREAD_STATUS_LABEL.waiting,
    tooltip: "Waiting on a reply",
    // Hourglass, not Clock3: the list row drops the label, so a status that
    // shares `inProgress`'s clock would leave hue as the only thing telling
    // the two apart. Native reached the same conclusion first.
    icon: Hourglass,
    className: "text-primary",
  },
  paused: {
    label: THREAD_STATUS_LABEL.paused,
    tooltip: "Thread is paused",
    icon: PauseCircle,
    className: "text-primary",
  },
  archived: {
    label: THREAD_STATUS_LABEL.archived,
    tooltip: "Thread is archived",
    icon: Archive2,
    className: "text-muted-foreground",
  },
} satisfies Record<
  string,
  {
    label: string;
    tooltip: string;
    icon: LucideIcon | React.FC<{ className?: string }>;
    className: string;
  }
>;

/** Width of every list-row mark. One value so the forms cannot drift apart —
 *  they read as one family, and titles align down the column, only while they
 *  match. */
const MARK_SIZE = "size-[8px]";

// The marks a row can wear, as a vocabulary rather than loose class strings.
// Hue says whether the thread is live; fill says whether it wants you.
/** Filled brand: live, and waiting on you. */
const BRAND_DOT = "bg-primary";
/** Hollow brand: live, but running on its own. */
const BRAND_RING = "border border-primary";
/** Hollow amber: the agent is actively working this thread. Its own hue
 *  because "running right now" is a different thing from "parked waiting on
 *  something", and at 8px a shared hue made the two indistinguishable. */
const AMBER_RING = "border border-amber-600";

/** Hollow muted: finished. Shares `MUTED_DOT`'s grey — one grey for the whole
 *  quiet tier, with the form (hollow vs filled) carrying the difference, since
 *  two steps apart only suggests a distinction that is not there. */
const MUTED_RING = "border border-zinc-400";
/** Filled muted: filed away. */
const MUTED_DOT = "bg-zinc-400";

/**
 * The mark a list row wears per status, keyed by how much the state asks of
 * you. A row is scanned rather than read, so the loudest form goes to the
 * states you act on, and completed gets a hollow ring of the same diameter —
 * present, but plainly finished.
 *
 * `paused` sits with `needsAttention` deliberately. It is an agent kill-switch
 * rather than a user action, and both things that set it park the thread on an
 * answer only the user can give: an inbound request awaiting yes or no
 * (`handleA2AKickoff`), and a suggested invitation awaiting accept or ignore
 * (`handleInvitation`, which pauses every invitation it does not mark ignored).
 * Reading either as dormant had it backwards.

 */
const compactStatusMark: Partial<Record<keyof typeof threadStatusConfig, string>> = {
  needsAttention: BRAND_DOT,
  paused: BRAND_DOT,
  inProgress: AMBER_RING,
  waiting: BRAND_RING,
  completed: MUTED_RING,
  archived: MUTED_DOT,
};

/**
 * Whether a row is waiting on the user, which is what bolds its title. Derived
 * from the mark table rather than tested separately, so the dot and the weight
 * cannot disagree about which rows those are.
 */
export const threadAwaitsUser = (thread: GetThreadResp): boolean =>
  compactStatusMark[getThreadStatus(thread)] === BRAND_DOT;

// ── Badge components ────────────────────────────────────────────────────

/**
 * A list-row attribute: a naked glyph at the nav icons' size, no chip and no
 * ground. Status is the only thing in the row that earns a mark; everything
 * else describes the thread rather than what it wants, and reads as an outline
 * beside it. These sit to the right of the mark.
 */
const CompactGlyph = ({
  icon: Icon,
  tooltip,
  className,
}: {
  icon: LucideIcon | React.FC<{ className?: string }>;
  tooltip: string;
  className?: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      {/* `role="img"` + the tooltip as the label: the span is not focusable, so
          a Tooltip alone names it for nobody, and the glyph carries no copy. */}
      <span
        className="inline-flex shrink-0 items-center justify-center"
        role="img"
        aria-label={tooltip}
      >
        <Icon className={cn("size-3.5", className)} />
      </span>
    </TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
);

export const ThreadStatusBadge = ({
  thread,
  compact = false,
  headerScale = false,
}: {
  thread: GetThreadResp;
  compact?: boolean;
  headerScale?: boolean;
}) => {
  // Show the archived chip inline on completed threads instead of the old
  // "hide the badge entirely" behaviour — keeps the header row aligned.
  const status = thread.status === "completed" ? ("archived" as const) : getThreadStatus(thread);
  if (!status) return null;

  const config = threadStatusConfig[status as keyof typeof threadStatusConfig];
  if (!config) return null;

  // `headerScale` is excluded: the detail header shows one thread, not a
  // column being scanned, so it keeps its glyph.
  const mark = compact && !headerScale ? compactStatusMark[status] : undefined;
  if (mark) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* `role="img"` + the tooltip as the label: the span is not focusable,
              so a Tooltip alone names it for nobody, and the mark carries no
              copy of its own anywhere in the DOM. */}
          <span
            className={cn("inline-block shrink-0 rounded-full", MARK_SIZE, mark)}
            role="img"
            aria-label={config.tooltip}
          />
        </TooltipTrigger>
        <TooltipContent>{config.tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <ThreadBadge
      icon={config.icon}
      label={config.label}
      tooltip={config.tooltip}
      compact={compact}
      headerScale={headerScale}
      className={config.className}
    />
  );
};

/** Badge for threads created by a triggered routine. */
export const RoutineTriggeredBadge = ({
  thread,
  compact = false,
  headerScale = false,
}: {
  thread: GetThreadResp;
  compact?: boolean;
  headerScale?: boolean;
}) => {
  if (!thread.labels?.includes("routineRun")) return null;

  if (compact && !headerScale) {
    return (
      <CompactGlyph
        icon={Zap}
        tooltip="Generated from a routine"
        className="text-muted-foreground"
      />
    );
  }

  return (
    <ThreadBadge
      icon={Zap}
      label="Routine"
      tooltip="Generated from a routine"
      compact={compact}
      headerScale={headerScale}
      className="text-muted-foreground"
    />
  );
};

/**
 * Badge for inbound invitations whose time window conflicts with an existing
 * event on the user's calendar. Reads from the transient `thread.card.labels`
 * (server-computed at fetch time), NOT `thread.labels` — the conflict state
 * depends on the live calendar and shouldn't be persisted.
 */
export const InvitationConflictBadge = ({
  thread,
  compact = false,
}: {
  thread: GetThreadResp;
  compact?: boolean;
}) => {
  if (!thread.card?.labels?.includes("invitation-conflict")) return null;

  if (compact) {
    return (
      <CompactGlyph
        icon={CalendarX}
        tooltip="This invite overlaps an event on your calendar"
        className="text-amber-600"
      />
    );
  }

  return (
    <ThreadBadge
      icon={CalendarX}
      label="Conflict"
      tooltip="This invite overlaps an event on your calendar"
      compact={compact}
      className="text-amber-600"
    />
  );
};

/**
 * Per-channel presentation for {@link ExternalChannelBadge}. Keyed by the
 * messaging channels only — `app` (in-app, the default) and `mcp` (programmatic)
 * intentionally have no entry, so the badge renders nothing for them.
 */
const externalChannelConfig: Partial<
  Record<
    ThreadChannel,
    { label: string; tooltip: string; icon: LucideIcon | React.FC<{ className?: string }> }
  >
> = {
  text_message: {
    label: "Text",
    tooltip: "This conversation happens over text message",
    icon: MessageCircle,
  },
  slack: { label: "Slack", tooltip: "This conversation happens in Slack", icon: SlackIcon },
  teams: {
    label: "Teams",
    tooltip: "This conversation happens in Microsoft Teams",
    icon: TeamsIcon,
  },
  whatsapp: {
    label: "WhatsApp",
    tooltip: "This conversation happens on WhatsApp",
    icon: WhatsAppIcon,
  },
};

/**
 * Badge marking a thread that is bridged to an external messaging channel
 * (text, Slack, Teams, WhatsApp), so the user can tell at a glance that
 * replying here sends a message out to that conversation. Renders nothing for
 * in-app (`app`) and programmatic (`mcp`) threads.
 */
export const ExternalChannelBadge = ({
  thread,
  compact = false,
  headerScale = false,
}: {
  thread: GetThreadResp;
  compact?: boolean;
  headerScale?: boolean;
}) => {
  const config = externalChannelConfig[thread.channel];
  if (!config) return null;

  if (compact && !headerScale) {
    // Brand marks (Slack, Teams, WhatsApp) bake in their own colors and ignore
    // this class; only the plain SMS glyph takes it.
    return (
      <CompactGlyph icon={config.icon} tooltip={config.tooltip} className="text-muted-foreground" />
    );
  }

  return (
    <ThreadBadge
      icon={config.icon}
      label={config.label}
      tooltip={config.tooltip}
      compact={compact}
      headerScale={headerScale}
      className="text-muted-foreground"
    />
  );
};

// ── Thread list styles ──────────────────────────────────────────────────

/** styles shared between thread row and card for interaction, e.g. selection and hover */
export const getThreadItemInteractionStyles = (showSelectionBorder: boolean) =>
  cn(
    "border outline-none",
    "cursor-pointer hover:border-border hover:shadow-lg transition-[border-color,box-shadow] duration-200",
    showSelectionBorder ? "!border-primary" : "border-popover",
  );

/**
 * A card at rest: the one definition of a card's surface, radius, and padding.
 * Every card in the app composes this — list rows and gallery cards alike — so
 * the shape is stated once rather than restated per page. Hosts add layout
 * (flex direction, gaps between their own children) and nothing else.
 *
 * Shadowless on purpose: a shadow is this app's clickable signal, so a card
 * that does not respond to a click must not carry one.
 *
 * Every value here is the home thread card's (`ThreadCard`), which is the
 * reference every other card follows: `rounded-2xl`, the popover surface, and
 * `p-3`. Keep them in step — this had drifted to `p-4`, which is why settings
 * cards read taller than the thread cards they were copying.
 */
export const cardSurfaceClass = "rounded-2xl bg-popover text-popover-foreground p-3 shadow-none";

/**
 * A card that responds to a click: the resting surface plus the thread card's
 * hover reveal. Defined here, next to the interaction styles it composes, so
 * the two treatments cannot drift apart. A card that isn't clickable takes
 * `cardSurfaceClass` instead — the hover shadow promises a click.
 */
export const clickableCardClass = cn(cardSurfaceClass, getThreadItemInteractionStyles(false));

/**
 * The gutter between cards stacked in a column, and the grid they sit in when
 * shown as cards. Both are `gap-3` — the same rhythm on both axes, and the same
 * number the card's own padding uses, so a list and a gallery of the same items
 * read as one family. Kept here beside the surface they space out: five pages
 * were each writing `flex flex-col gap-3`, which is how a rhythm drifts.
 */
export const cardListClass = "flex flex-col gap-3";

/**
 * The compact row's surface: a 44px white rounded row.
 *
 * `ThreadRow` draws it below `md` and flattens from there; the task list draws
 * it at every width, because its rows keep their height where a thread's
 * shrink to 28px. Stated once because the two lists sit a tab apart and are
 * read the same way — a line in one should be the same object as a line in the
 * other, and two copies of a surface is how that stops being true.
 *
 * The transparent border is not spacing: the nav links above these lists carry
 * a 1px border, and matching the box model keeps the marks on their
 * centerline. A host that draws its own border simply sets the colour.
 */
export const compactRowClass =
  "flex h-11 w-full cursor-pointer flex-row items-center rounded-xl border border-transparent bg-popover px-3 text-left transition-colors";

/**
 * Card-view grid: auto-fill with an 18rem card floor instead of fixed
 * breakpoint columns — the grid drops a column whenever cards would get
 * squeezed below the floor, which also protects against the sidebar narrowing
 * the container in ways viewport breakpoints can't see. min(18rem,100%) lets
 * the single remaining column shrink under the floor instead of overflowing a
 * container narrower than 18rem.
 */
export const galleryCardsClass =
  "grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(18rem,100%),1fr))]";
